import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { api, API } from "@/lib/api";
import { useSuggestions, AutocompleteInput, refreshSuggestions } from "@/lib/suggestions";
import { SERIES_TYPES, buildTypeOptions, resolveTypeChoice } from "@/lib/project_types";

export default function LibraryPage() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState("");
  const [bulkReport, setBulkReport] = useState(null);

  // Debounce the search input (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchQ.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  const load = async () => {
    const params = new URLSearchParams();
    if (filterType) params.set("project_type", filterType);
    if (debouncedQ) params.set("q", debouncedQ);
    const url = params.toString() ? `/references?${params}` : `/references`;
    const r = await api.get(url);
    setRows(r.data || []);
  };

  useEffect(() => { api.get("/references/types").then((r) => setTypes(r.data || [])); }, []);
  useEffect(() => { load(); }, [filterType, debouncedQ]); // eslint-disable-line

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true); setError(""); setProgress(0);
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const fd = new FormData();
        fd.append("file", f);
        await axios.post(`${API}/references/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (ev) => {
            const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 50;
            setProgress(Math.round(((i + pct / 100) / files.length) * 100));
          },
          timeout: 180000,
        });
      }
      await load();
      e.target.value = "";
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Error subiendo");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onDelete = async (r) => {
    if (!window.confirm(`¿Eliminar "${r.title}" de la biblioteca?`)) return;
    await api.delete(`/references/${r.id}`);
    load();
  };

  const runBulkAi = async () => {
    if (!window.confirm("Claude leerá todas las referencias con metadatos vacíos y los completará. Puede tardar ~1-2 min por documento. ¿Continuar?")) return;
    setBulkRunning(true); setBulkReport(null);
    setBulkProgress("solicitando...");
    try {
      const r = await api.post(`/references/ai/metadata/bulk`, { force: false }, { timeout: 1800000 });
      setBulkReport(r.data);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBulkRunning(false);
      setBulkProgress("");
    }
  };

  const stats = useMemo(() => {
    const total = rows.length;
    const totalChars = rows.reduce((s, r) => s + (r.char_count || 0), 0);
    const totalPages = rows.reduce((s, r) => s + (r.page_count || 0), 0);
    return { total, totalChars, totalPages };
  }, [rows]);

  return (
    <div className="min-h-screen bg-gb-bg text-[#2C2B29]">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#2C2B29]/10 bg-[#2C2B29] text-white">
        <img src="/brand/isotype.png" alt=""
             className="pointer-events-none absolute -right-12 -top-10 h-[420px] w-auto opacity-[0.08]" />
        <div className="relative mx-auto max-w-7xl px-4 py-10 sm:px-8 sm:py-12">
          <p className="text-[11px] font-medium uppercase tracking-[0.45em] text-[#941620]">
            grabarte · biblioteca de referencias
          </p>
          <h1 className="mt-3 max-w-3xl font-display text-3xl leading-tight md:text-4xl">
            Tu archivo personal de dossieres.
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/65 md:text-base">
            Sube dossieres pasados — propios o de obras que admiras (PDF, DOCX, PPTX). Cuando uses la IA para escribir,
            podrás marcar una o varias referencias y Claude tomará su tono, registro y nivel como inspiración.
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <label className="group cursor-pointer rounded-full bg-[#26775E] px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-white transition hover:bg-[#1c5c48]"
                   data-testid="upload-label">
              {uploading ? `Subiendo ${progress}%…` : "+ Subir documento"}
              <input type="file" accept=".pdf,.docx,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                     multiple onChange={onUpload}
                     data-testid="upload-input"
                     className="hidden" disabled={uploading} />
            </label>
            <button type="button" onClick={runBulkAi} disabled={bulkRunning}
                    data-testid="bulk-ai-btn"
                    className="rounded-full border border-white/25 px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-white/90 transition hover:border-white disabled:opacity-50">
              {bulkRunning ? `Procesando ${bulkProgress}…` : "✨ Completar metadatos con IA"}
            </button>
            {uploading && (
              <div className="h-1.5 w-48 overflow-hidden rounded-full bg-white/15">
                <div className="h-full bg-[#941620]" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
          {error && <p className="mt-3 text-sm text-red-300">{error}</p>}
          {bulkReport && (
            <div className="mt-3 max-w-2xl rounded-lg border border-white/15 bg-white/[0.04] p-3 text-[11px] text-white/75" data-testid="bulk-report">
              <p className="font-semibold text-white">Resultado del autocompletado</p>
              <ul className="mt-1 space-y-0.5">
                {bulkReport.results.slice(0, 12).map((r, i) => (
                  <li key={i}>
                    {r.updated ? (
                      <span className="text-[#941620]">✓ {r.title} → {r.updated.filter((k) => k !== "updated_at").join(", ")}</span>
                    ) : r.error ? (
                      <span className="text-red-300">✗ {r.title}: {r.error.slice(0, 80)}</span>
                    ) : (
                      <span className="text-white/45">— {r.title}: {r.skipped}</span>
                    )}
                  </li>
                ))}
                {bulkReport.results.length > 12 && <li className="text-white/45">…y {bulkReport.results.length - 12} más</li>}
              </ul>
            </div>
          )}

          <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-3">
            <Stat label="Referencias" value={stats.total} />
            <Stat label="Páginas totales" value={stats.totalPages} />
            <Stat label="Texto indexado" value={`${(stats.totalChars / 1000).toFixed(1)}k caracteres`} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-8 sm:py-10">
        {/* Search bar */}
        <div className="mb-5">
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#2C2B29]/55">
              Buscar
            </span>
            <div className="relative mt-2">
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
                   className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2C2B29]/40">
                <circle cx="9" cy="9" r="6" />
                <path d="M14 14l4 4" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                data-testid="library-search"
                placeholder="Director, productora, título o cualquier palabra del texto…"
                className="w-full rounded-full border border-[#2C2B29]/15 bg-white py-3 pl-10 pr-12 text-sm outline-none placeholder:text-[#2C2B29]/40 focus:border-[#26775E] focus:ring-2 focus:ring-[#26775E]/15"
              />
              {searchQ && (
                <button type="button" onClick={() => setSearchQ("")}
                        data-testid="library-search-clear"
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#2C2B29]/40 hover:bg-[#F1EEE5] hover:text-[#2C2B29]">×</button>
              )}
            </div>
            {debouncedQ && (
              <p className="mt-1.5 text-[11px] text-[#2C2B29]/55" data-testid="library-search-result-count">
                {rows.length === 0
                  ? `Sin resultados para "${debouncedQ}"`
                  : `${rows.length} ${rows.length === 1 ? "resultado" : "resultados"} para "${debouncedQ}"`}
              </p>
            )}
          </label>
        </div>

        {/* Filter */}
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#2C2B29]/55">Filtrar por tipo</p>
          <button onClick={() => setFilterType("")}
                  data-testid="filter-all"
                  className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] transition ${filterType === "" ? "bg-[#2C2B29] text-white" : "bg-white text-[#2C2B29]/70 hover:bg-[#F1EEE5]"}`}>
            Todas
          </button>
          {types.map((t) => (
            <button key={t} onClick={() => setFilterType(t)}
                    data-testid={`filter-${t}`}
                    className={`rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] transition ${filterType === t ? "bg-[#2C2B29] text-white" : "bg-white text-[#2C2B29]/70 hover:bg-[#F1EEE5]"}`}>
              {t}
            </button>
          ))}
        </div>

        {/* Grid */}
        {rows.length === 0 ? (
          <p className="rounded-xl border border-dashed border-[#2C2B29]/15 bg-white p-12 text-center text-sm text-[#2C2B29]/55">
            Aún no hay referencias en esta vista. Sube un PDF para empezar.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="references-grid">
            {rows.map((r) => (
              <article key={r.id} data-testid={`ref-card-${r.id}`}
                       className="group flex flex-col overflow-hidden rounded-2xl border border-[#2C2B29]/10 bg-white p-5 transition hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#26775E]">
                    {r.project_type}
                  </p>
                  <button onClick={() => onDelete(r)}
                          data-testid={`ref-delete-${r.id}`}
                          className="rounded-full p-1 text-[#2C2B29]/30 transition hover:bg-red-50 hover:text-red-600">×</button>
                </div>
                <h3 className="mt-2 font-display text-xl leading-snug">{r.title}</h3>
                <p className="mt-1 text-[12px] text-[#2C2B29]/55">
                  {[r.director, r.year, r.country, r.episodes ? `${r.episodes} cap.` : ""].filter(Boolean).join(" · ") || "—"}
                </p>

                {r.preview ? (
                  <p className="mt-3 line-clamp-4 text-[13px] leading-relaxed text-[#2C2B29]/75 font-editorial">
                    {r.preview}
                  </p>
                ) : (
                  <p className="mt-3 rounded border border-dashed border-[#941620]/40 bg-[#941620]/5 p-2 text-[11px] text-[#2C2B29]/60">
                    {r.char_count === 0
                      ? "PDF sin texto seleccionable (escaneado). Abre 'Editar' y pulsa 'Re-extraer con OCR'."
                      : "No se detectaron secciones con encabezados estándar — el texto completo sigue disponible para la IA."}
                  </p>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-[#2C2B29]/5 pt-3">
                  <span className="rounded bg-[#F1EEE5] px-2 py-0.5 text-[10px] font-mono text-[#2C2B29]/65">
                    {r.page_count}pp · {(r.char_count / 1000).toFixed(1)}k chars
                  </span>
                  {(r.tags || []).map((t) => (
                    <span key={t} className="rounded bg-[#26775E]/10 px-2 py-0.5 text-[10px] font-medium text-[#26775E]">#{t}</span>
                  ))}
                </div>

                <div className="mt-3 flex gap-2">
                  <button onClick={() => setViewing(r)}
                          data-testid={`ref-view-${r.id}`}
                          className="flex-1 rounded-full border border-[#2C2B29]/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#2C2B29]/75 hover:border-[#26775E] hover:text-[#26775E]">
                    Leer
                  </button>
                  <button onClick={() => setEditing(r)}
                          data-testid={`ref-edit-${r.id}`}
                          className="flex-1 rounded-full bg-[#2C2B29] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white hover:bg-[#26775E]">
                    Editar
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {viewing && (
        <Viewer ref_={viewing} onClose={() => setViewing(null)} />
      )}
      {editing && (
        <Editor ref_={editing} types={types}
                onSaved={() => { setEditing(null); load(); }}
                onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
    <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-white/45">{label}</p>
    <p className="mt-1 font-display text-xl tabular-nums text-white">{value}</p>
  </div>
);

function Viewer({ ref_, onClose }) {
  const [full, setFull] = useState(null);
  const isPdf = (ref_.source_filename || "").toLowerCase().endsWith(".pdf");
  // Remember last page per reference (localStorage)
  const storageKey = `ref-last-page:${ref_.id}`;
  const lastPage = (() => {
    try { return Number(localStorage.getItem(storageKey)) || 1; } catch { return 1; }
  })();
  const [tab, setTab] = useState(isPdf ? "pdf" : "sections");
  const [currentPage, setCurrentPage] = useState(lastPage);

  useEffect(() => {
    api.get(`/references/${ref_.id}`).then((r) => setFull(r.data));
  }, [ref_.id]);

  const fileUrl = `${API}/references/${ref_.id}/file#page=${currentPage}&zoom=page-fit`;
  const hasSections = full?.sections && Object.keys(full.sections).length > 0;

  // When closing or switching tabs, attempt to capture the current page from iframe URL
  const persistPage = () => {
    try {
      const iframe = document.querySelector("[data-testid=viewer-pdf-iframe]");
      if (!iframe) return;
      // Some PDF viewers expose the current page via URL fragment after navigation
      const src = iframe.contentWindow?.location?.href || iframe.src || "";
      const m = src.match(/[#&]page=(\d+)/);
      if (m) {
        const p = Number(m[1]);
        if (p > 0) {
          localStorage.setItem(storageKey, String(p));
          setCurrentPage(p);
        }
      }
    } catch (err) { console.debug("persistPage: cross-origin, no se pudo leer la página actual del PDF", err); }
  };

  const close = () => { persistPage(); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2C2B29]/80 p-3 backdrop-blur"
         onClick={close} data-testid="ref-viewer">
      <div onClick={(e) => e.stopPropagation()}
           className="flex h-[95vh] w-full max-w-7xl flex-col overflow-hidden rounded-2xl bg-[#FAFAF6] shadow-2xl">
        <header className="flex items-center justify-between border-b border-[#2C2B29]/10 bg-white px-6 py-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-[#26775E]">Referencia</p>
            <h2 className="mt-0.5 truncate font-display text-2xl">{ref_.title}</h2>
            <p className="text-[12px] text-[#2C2B29]/55">
              {[ref_.project_type, ref_.director, ref_.year, ref_.source_filename].filter(Boolean).join(" · ")}
              {isPdf && lastPage > 1 && (
                <span className="ml-2 rounded bg-[#941620]/20 px-1.5 py-0.5 text-[10px] font-semibold text-[#2C2B29]/75" data-testid="last-page-pill">
                  retomando pág. {lastPage}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isPdf && (
              <div className="flex rounded-full bg-[#F1EEE5] p-0.5">
                <button onClick={() => setTab("pdf")} data-testid="viewer-tab-pdf"
                        className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] transition ${tab === "pdf" ? "bg-[#2C2B29] text-white" : "text-[#2C2B29]/60"}`}>
                  PDF original
                </button>
                <button onClick={() => { persistPage(); setTab("sections"); }} data-testid="viewer-tab-sections"
                        className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] transition ${tab === "sections" ? "bg-[#2C2B29] text-white" : "text-[#2C2B29]/60"}`}>
                  Texto extraído
                </button>
              </div>
            )}
            <a href={`${API}/references/${ref_.id}/file`} download={ref_.source_filename}
               data-testid="viewer-download"
               className="rounded-full border border-[#2C2B29]/15 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#2C2B29]/70 hover:border-[#26775E] hover:text-[#26775E]">
              Descargar
            </a>
            <button onClick={close} data-testid="viewer-close"
                    className="rounded-full p-2 text-[#2C2B29]/50 hover:bg-[#F1EEE5]">×</button>
          </div>
        </header>

        <div className="flex-1 overflow-hidden">
          {!isPdf || tab === "sections" ? (
            <div className="h-full overflow-y-auto p-6 font-editorial text-[14px] leading-[1.65] text-[#2C2B29]/85">
              {!full ? (
                <p className="text-[#2C2B29]/50">Cargando…</p>
              ) : hasSections ? (
                Object.entries(full.sections).map(([k, v]) => (
                  <section key={k} className="mx-auto mb-6 max-w-3xl" data-testid={`viewer-section-${k}`}>
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.4em] text-[#26775E]">{labelOf(k)}</p>
                    <div className="whitespace-pre-wrap">{v}</div>
                  </section>
                ))
              ) : (
                <div className="mx-auto max-w-3xl whitespace-pre-wrap text-[#2C2B29]/70" data-testid="viewer-fulltext">
                  {full.text_full || "Sin texto extraíble. Si el PDF es escaneado puedes pulsar 'Re-extraer con OCR' en Editar."}
                </div>
              )}
            </div>
          ) : (
            <iframe src={fileUrl}
                    data-testid="viewer-pdf-iframe"
                    title={ref_.title}
                    onLoad={persistPage}
                    className="h-full w-full border-0" />
          )}
        </div>

        {isPdf && tab === "pdf" && (
          <footer className="flex items-center justify-between border-t border-[#2C2B29]/10 bg-white px-6 py-2 text-[11px] text-[#2C2B29]/60" data-testid="viewer-footer">
            <span>La última página visitada se guarda automáticamente al cerrar.</span>
            <button type="button" onClick={() => { localStorage.removeItem(storageKey); setCurrentPage(1); }}
                    data-testid="reset-page"
                    className="rounded-full border border-[#2C2B29]/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] hover:border-[#26775E] hover:text-[#26775E]">
              Reiniciar pág.
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}

function Editor({ ref_, types, onSaved, onClose }) {
  const sugg = useSuggestions();
  const [form, setForm] = useState({
    title: ref_.title || "",
    project_type: ref_.project_type || "Largo Documental",
    director: ref_.director || "",
    production: ref_.production || "",
    year: ref_.year || "",
    country: ref_.country || "",
    duration: ref_.duration || "",
    episodes: ref_.episodes || "",
    tags: (ref_.tags || []).join(", "),
    notes: ref_.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [ocrRunning, setOcrRunning] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [aiRunning, setAiRunning] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      await api.patch(`/references/${ref_.id}`, payload);
      refreshSuggestions();
      onSaved();
    } finally { setSaving(false); }
  };

  const runAi = async () => {
    setAiRunning(true); setAiResult(null);
    try {
      const r = await api.post(`/references/${ref_.id}/ai/metadata`, {}, { timeout: 90000 });
      const data = r.data || {};
      // Apply only fields where current form is empty OR user confirms overwrite of all
      const merged = { ...form };
      let overwrites = 0;
      ["title", "project_type", "director", "production", "year", "country", "duration", "episodes"].forEach((k) => {
        if (data[k] && !merged[k]) { merged[k] = data[k]; }
        else if (data[k] && merged[k] !== data[k]) { overwrites++; }
      });
      if ((data.tags || []).length > 0 && !merged.tags) {
        merged.tags = (data.tags || []).join(", ");
      }
      setForm(merged);
      setAiResult({ ok: true, overwrites, reasoning: data.reasoning, proposed: data });
    } catch (e) {
      setAiResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setAiRunning(false);
    }
  };

  const applyAiAll = () => {
    if (!aiResult?.proposed) return;
    const d = aiResult.proposed;
    setForm({
      ...form,
      title: d.title || form.title,
      project_type: d.project_type || form.project_type,
      director: d.director || form.director,
      production: d.production || form.production,
      year: d.year || form.year,
      country: d.country || form.country,
      duration: d.duration || form.duration,
      episodes: d.episodes || form.episodes,
      tags: (d.tags || []).join(", ") || form.tags,
    });
  };

  const runOcr = async () => {
    if (!window.confirm("Re-extraer con OCR puede tardar 1-2 min. ¿Continuar?")) return;
    setOcrRunning(true); setOcrResult(null);
    try {
      const r = await api.post(`/references/${ref_.id}/reextract`, {}, { timeout: 240000 });
      setOcrResult(r.data);
    } catch (e) {
      setOcrResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setOcrRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2C2B29]/75 p-4 backdrop-blur"
         onClick={onClose} data-testid="ref-editor">
      <form onClick={(e) => e.stopPropagation()} onSubmit={save}
            className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="border-b border-[#2C2B29]/10 px-6 py-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-[#26775E]">Editar metadatos</p>
          <h2 className="mt-0.5 font-display text-2xl">{ref_.title}</h2>
        </header>
        <div className="space-y-3 p-6">
          {/* AI auto-fill */}
          <div className="rounded-lg border border-[#26775E]/30 bg-[#26775E]/5 p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#26775E]">✨ Completar con IA</p>
                <p className="mt-1 text-[12px] text-[#2C2B29]/65">
                  Claude leerá el texto extraído y te propondrá título, tipo, director, año, país, tags…
                </p>
              </div>
              <button type="button" onClick={runAi} disabled={aiRunning}
                      data-testid="edit-ai-fill"
                      className="shrink-0 rounded-full bg-[#26775E] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-[#1c5c48] disabled:opacity-50">
                {aiRunning ? "Leyendo…" : "Inferir metadatos"}
              </button>
            </div>
            {aiResult?.ok && (
              <div className="mt-3 rounded border border-[#26775E]/20 bg-white p-3 text-[12px] text-[#2C2B29]/80" data-testid="ai-result">
                {aiResult.reasoning && <p className="italic text-[#2C2B29]/60">"{aiResult.reasoning}"</p>}
                <p className="mt-2">
                  Se han rellenado <strong>los campos vacíos</strong>.
                  {aiResult.overwrites > 0 && (
                    <>
                      {" "}Hay <strong>{aiResult.overwrites}</strong> campo(s) ya con valor distinto a la propuesta IA.
                      <button type="button" onClick={applyAiAll} data-testid="ai-overwrite-all"
                              className="ml-2 rounded-full border border-[#26775E] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#26775E] hover:bg-[#26775E] hover:text-white">
                        Sobrescribir todo
                      </button>
                    </>
                  )}
                </p>
                {aiResult.proposed?.tags?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {aiResult.proposed.tags.map((t) => (
                      <span key={t} className="rounded bg-[#26775E]/10 px-1.5 py-0.5 text-[10px] text-[#26775E]">#{t}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
            {aiResult?.error && <p className="mt-2 text-[12px] text-red-600">{aiResult.error}</p>}
          </div>

          <Field label="Título" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="edit-title" />
          <Field label="Tipo" select value={form.project_type}
                 options={buildTypeOptions(form.project_type)}
                 onChange={(v) => {
                   const next = resolveTypeChoice(v);
                   if (next) setForm({ ...form, project_type: next });
                 }} testid="edit-type" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Director/a" value={form.director} onChange={(v) => setForm({ ...form, director: v })} testid="edit-director" suggestions={sugg.directors} />
            <Field label="Productora" value={form.production} onChange={(v) => setForm({ ...form, production: v })} testid="edit-production" suggestions={sugg.producers} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Año" value={form.year} onChange={(v) => setForm({ ...form, year: v })} testid="edit-year" />
            <Field label="País" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="edit-country" />
            <Field label="Duración" value={form.duration} onChange={(v) => setForm({ ...form, duration: v })} testid="edit-duration" />
          </div>
          {SERIES_TYPES.has(form.project_type) && (
            <Field label="Nº de capítulos" value={form.episodes}
                   onChange={(v) => setForm({ ...form, episodes: v })}
                   testid="edit-episodes" />
          )}
          <Field label="Tags (separadas por coma)" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} testid="edit-tags" />
          <Field label="Notas privadas" textarea value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="edit-notes" />

          {(ref_.source_filename || "").toLowerCase().endsWith(".pdf") && (
            <div className="rounded-lg border border-[#941620]/40 bg-[#941620]/5 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2C2B29]/70">Re-extracción</p>
              <p className="mt-1 text-[12px] text-[#2C2B29]/65">
                Útil si el PDF era escaneado y se subió sin OCR, o si quieres volver a procesar el texto.
              </p>
              <button type="button" onClick={runOcr} disabled={ocrRunning}
                      data-testid="edit-ocr"
                      className="mt-3 rounded-full bg-[#2C2B29] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-[#26775E] disabled:opacity-50">
                {ocrRunning ? "Procesando OCR…" : "Re-extraer con OCR"}
              </button>
              {ocrResult && !ocrResult.error && (
                <p className="mt-3 text-[12px] text-[#26775E]">
                  ✓ Re-extracción completada: <strong>{ocrResult.char_count.toLocaleString()}</strong> caracteres ({ocrResult.method}). Secciones detectadas: {(ocrResult.sections_detected || []).join(", ") || "—"}
                </p>
              )}
              {ocrResult?.error && <p className="mt-3 text-[12px] text-red-600">{ocrResult.error}</p>}
            </div>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-t border-[#2C2B29]/10 bg-[#FAFAF6] px-6 py-4">
          <button type="button" onClick={onClose}
                  className="rounded-full border border-[#2C2B29]/20 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2C2B29]/65 hover:bg-white">
            Cancelar
          </button>
          <button type="submit" disabled={saving} data-testid="edit-save"
                  className="rounded-full bg-[#26775E] px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-[#1c5c48] disabled:opacity-50">
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </footer>
      </form>
    </div>
  );
}

const Field = ({ label, value, onChange, select, options, textarea, testid, suggestions }) => (
  <label className="block">
    <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2C2B29]/55">{label}</span>
    {select ? (
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
              className="mt-1 w-full rounded-md border border-[#2C2B29]/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#26775E]">
        {(options || []).map((o) => <option key={o}>{o}</option>)}
      </select>
    ) : textarea ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testid}
                className="mt-1 w-full rounded-md border border-[#2C2B29]/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#26775E]" />
    ) : suggestions ? (
      <AutocompleteInput value={value} onChange={onChange} options={suggestions}
        data-testid={testid}
        className="mt-1 w-full rounded-md border border-[#2C2B29]/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#26775E]" />
    ) : (
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
             className="mt-1 w-full rounded-md border border-[#2C2B29]/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#26775E]" />
    )}
  </label>
);

function labelOf(k) {
  return {
    logline: "Logline",
    sinopsis: "Sinopsis",
    nota_intenciones: "Nota de intenciones",
    vinculo: "Vínculo con la historia",
    contexto: "Contexto",
    personajes: "Personajes",
    tratamiento: "Tratamiento visual",
    referencias: "Referencias audiovisuales",
    biofilmo: "Biofilmografía",
    produccion: "Producción",
  }[k] || k;
}
